import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
    CreditCard, CheckCircle2, XCircle, RefreshCw, Loader2, Zap,
    Link as LinkIcon, Settings, Trash2, Copy, ExternalLink,
    TrendingUp, ShoppingCart, RotateCcw, DollarSign, Eye, Plus,
    AlertTriangle, Radio, FileText
} from 'lucide-react';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n ?? 0);
const fmtDate = (s) => s ? new Date(s).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const StatusBadge = ({ status }) => {
    const map = {
        paid: ['bg-green-100 text-green-800', 'Pagado'],
        completed: ['bg-green-100 text-green-800', 'Completado'],
        pending: ['bg-yellow-100 text-yellow-800', 'Pendiente'],
        pending_payment: ['bg-yellow-100 text-yellow-800', 'Pendiente pago'],
        refunded: ['bg-blue-100 text-blue-800', 'Reembolsado'],
        cancelled: ['bg-gray-100 text-gray-600', 'Cancelado'],
        failed: ['bg-red-100 text-red-800', 'Fallido'],
        delivered: ['bg-purple-100 text-purple-800', 'Entregado'],
        released: ['bg-teal-100 text-teal-800', 'Liberado'],
    };
    const [cls, label] = map[status] ?? ['bg-gray-100 text-gray-600', status ?? '—'];
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}>{label}</span>;
};

// ─── Dashboard tab ────────────────────────────────────────────────────────────
function DashboardTab({ settings, onSave }) {
    const [stats, setStats]     = useState(null);
    const [testing, setTesting] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try { setStats((await api.get('/admin/ecartpay/stats')).data); } catch { setStats(null); }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const testConn = async () => {
        setTesting(true);
        try {
            const { data } = await api.post('/admin/ecartpay/test-connection');
            toast.success(data.message);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error de conexión');
        }
        setTesting(false);
    };

    const StatCard = ({ icon: Icon, label, value, sub, color = 'text-foreground' }) => (
        <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
                <Icon className={`h-5 w-5 ${color}`} />
                <span className="text-sm text-muted-foreground">{label}</span>
            </div>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Status banner */}
            <div className={`rounded-xl border p-4 flex items-center gap-3 ${settings?.has_public_key ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                {settings?.has_public_key
                    ? <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                    : <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
                }
                <div>
                    <div className="font-medium text-sm">
                        {settings?.has_public_key
                            ? `EcartPay configurado · ${settings.environment === 'sandbox' ? '🧪 Sandbox' : '🚀 Producción'}`
                            : 'EcartPay no configurado'
                        }
                    </div>
                    {settings?.has_public_key && (
                        <div className="text-xs text-muted-foreground">Clave pública: {settings.public_key_hint}</div>
                    )}
                </div>
                <div className="ml-auto flex gap-2">
                    {settings?.has_public_key && (
                        <Button size="sm" variant="outline" onClick={testConn} disabled={testing}>
                            {testing ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Zap className="h-3 w-3 mr-1" />}
                            Probar conexión
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                        Actualizar
                    </Button>
                </div>
            </div>

            {/* Stats */}
            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : stats ? (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard icon={DollarSign} label="Ingresos totales" value={fmt(stats.revenue)} color="text-green-600" />
                    <StatCard icon={ShoppingCart} label="Órdenes totales" value={stats.total_orders} sub={`${stats.paid_orders} pagadas`} />
                    <StatCard icon={TrendingUp} label="Comisiones" value={fmt(stats.commission)} color="text-blue-600" />
                    <StatCard icon={RotateCcw} label="Reembolsos" value={stats.refunds} />
                </div>
            ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">No se pudieron cargar estadísticas</div>
            )}

            {/* Info cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-card p-5">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Radio className="h-4 w-4 text-primary" />Webhook URL</h3>
                    <div className="bg-muted rounded-lg p-3 font-mono text-xs break-all">
                        {window.location.origin}/api/webhook/ecartpay
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                        Configura esta URL como <code>notify_url</code> en tu panel de EcartPay para recibir notificaciones de pago.
                    </p>
                </div>
                <div className="rounded-xl border bg-card p-5">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" />Métodos de pago soportados</h3>
                    <div className="flex flex-wrap gap-2">
                        {['Tarjeta de crédito', 'Tarjeta de débito', 'SPEI / Transferencia', 'Efectivo OXXO', 'E-wallet', 'Meses sin intereses'].map(m => (
                            <span key={m} className="text-[11px] bg-muted px-2 py-1 rounded-full">{m}</span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Orders tab ───────────────────────────────────────────────────────────────
function OrdersTab() {
    const [orders, setOrders]       = useState([]);
    const [loading, setLoading]     = useState(true);
    const [page, setPage]           = useState(1);
    const [selected, setSelected]   = useState(null);
    const [refunding, setRefunding] = useState(false);
    const [refAmt, setRefAmt]       = useState('');
    const [refReason, setRefReason] = useState('requested_by_customer');

    const load = useCallback(async (p = 1) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/ecartpay/orders?page=${p}`);
            const list = Array.isArray(data) ? data : (data?.data || data?.orders || []);
            setOrders(list);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cargar órdenes');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(page); }, [load, page]);

    const doRefund = async (epOrderId) => {
        setRefunding(true);
        try {
            await api.post(`/admin/ecartpay/orders/${epOrderId}/refund`, {
                ecartpay_order_id: epOrderId,
                amount: refAmt ? parseFloat(refAmt) : undefined,
                reason: refReason,
            });
            toast.success('Reembolso procesado exitosamente');
            setSelected(null);
            setRefAmt('');
            load(page);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al procesar reembolso');
        }
        setRefunding(false);
    };

    const reasons = [
        { value: 'requested_by_customer', label: 'Solicitado por cliente' },
        { value: 'fraudulent', label: 'Fraudulento' },
        { value: 'duplicate', label: 'Duplicado' },
        { value: 'product_not_received', label: 'Producto no recibido' },
        { value: 'product_unacceptable', label: 'Producto inaceptable' },
    ];

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-semibold">Órdenes EcartPay</h2>
                <Button size="sm" variant="outline" onClick={() => load(page)} disabled={loading}>
                    <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualizar
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : orders.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">Sin órdenes aún</div>
            ) : (
                <div className="rounded-xl border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 text-xs text-muted-foreground">
                            <tr>
                                <th className="text-left px-4 py-3">ID EcartPay</th>
                                <th className="text-left px-4 py-3">Referencia</th>
                                <th className="text-left px-4 py-3">Total</th>
                                <th className="text-left px-4 py-3">Estado</th>
                                <th className="text-left px-4 py-3">Fecha</th>
                                <th className="text-left px-4 py-3">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {orders.map((o) => (
                                <tr key={o.id || o._id} className="hover:bg-muted/30 transition-colors">
                                    <td className="px-4 py-3 font-mono text-xs">{(o.id || o._id || '').slice(0, 16)}…</td>
                                    <td className="px-4 py-3 text-muted-foreground">{o.reference || o.number || '—'}</td>
                                    <td className="px-4 py-3 font-medium">{fmt(o.totals?.total ?? o.amount ?? o.total)}</td>
                                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(o.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-1">
                                            <Button size="sm" variant="ghost" className="h-7 text-xs"
                                                onClick={() => setSelected(o)}>
                                                <Eye className="h-3 w-3 mr-1" />Ver
                                            </Button>
                                            {['paid', 'completed'].includes(o.status) && (
                                                <Button size="sm" variant="ghost"
                                                    className="h-7 text-xs text-blue-600 hover:text-blue-700"
                                                    onClick={() => setSelected({ ...o, _refundMode: true })}>
                                                    <RotateCcw className="h-3 w-3 mr-1" />Reembolso
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20 text-sm">
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                        <span className="text-muted-foreground">Página {page}</span>
                        <Button size="sm" variant="outline" disabled={orders.length < 50} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
                    </div>
                </div>
            )}

            {/* Order detail / refund modal */}
            {selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-2xl shadow-2xl w-full max-w-lg m-4 overflow-hidden">
                        <div className="p-5 border-b flex items-center justify-between">
                            <h3 className="font-semibold">{selected._refundMode ? 'Procesar reembolso' : 'Detalle de orden'}</h3>
                            <Button size="sm" variant="ghost" onClick={() => { setSelected(null); setRefAmt(''); }}>✕</Button>
                        </div>
                        <div className="p-5 space-y-4">
                            {selected._refundMode ? (
                                <>
                                    <p className="text-sm text-muted-foreground">Orden: <span className="font-mono">{selected.id}</span></p>
                                    <p className="text-sm">Total: <span className="font-semibold">{fmt(selected.totals?.total ?? selected.amount ?? selected.total)}</span></p>
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground">Monto (vacío = reembolso total)</label>
                                        <Input type="number" placeholder="Ej: 500.00" value={refAmt} onChange={e => setRefAmt(e.target.value)} className="mt-1" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-medium text-muted-foreground">Razón</label>
                                        <select className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-background"
                                            value={refReason} onChange={e => setRefReason(e.target.value)}>
                                            {reasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                        </select>
                                    </div>
                                    <Button className="w-full" onClick={() => doRefund(selected.id)} disabled={refunding}>
                                        {refunding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                                        Confirmar reembolso
                                    </Button>
                                </>
                            ) : (
                                <div className="space-y-2 text-sm">
                                    {[
                                        ['ID', selected.id],
                                        ['Estado', <StatusBadge key="s" status={selected.status} />],
                                        ['Total', fmt(selected.totals?.total ?? selected.amount ?? selected.total)],
                                        ['Referencia', selected.reference || selected.number || '—'],
                                        ['Creado', fmtDate(selected.created_at)],
                                        ['Actualizado', fmtDate(selected.updated_at)],
                                    ].map(([k, v]) => (
                                        <div key={k} className="flex justify-between py-1 border-b last:border-0">
                                            <span className="text-muted-foreground">{k}</span>
                                            <span className="font-medium">{v}</span>
                                        </div>
                                    ))}
                                    {selected.pay_link && (
                                        <a href={selected.pay_link} target="_blank" rel="noreferrer"
                                            className="flex items-center gap-1 text-primary text-xs mt-2">
                                            <ExternalLink className="h-3 w-3" />Ver enlace de pago
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Payment Links tab ────────────────────────────────────────────────────────
function PaymentLinksTab() {
    const [links, setLinks]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm]       = useState({ name: '', currency: 'MXN', item_name: '', item_price: '', item_qty: '1' });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/ecartpay/payment-links');
            setLinks(Array.isArray(data) ? data : (data?.data || data?.payment_links || []));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cargar links');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!form.name || !form.item_name || !form.item_price) {
            toast.error('Completa los campos requeridos'); return;
        }
        setCreating(true);
        try {
            await api.post('/admin/ecartpay/payment-links', {
                name: form.name,
                currency: form.currency,
                items: [{ name: form.item_name, quantity: parseInt(form.item_qty) || 1, price: parseFloat(form.item_price) }],
            });
            toast.success('Link creado exitosamente');
            setShowForm(false);
            setForm({ name: '', currency: 'MXN', item_name: '', item_price: '', item_qty: '1' });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al crear link');
        }
        setCreating(false);
    };

    const del = async (id) => {
        if (!window.confirm('¿Eliminar este link de pago?')) return;
        try {
            await api.delete(`/admin/ecartpay/payment-links/${id}`);
            toast.success('Link eliminado');
            load();
        } catch (e) {
            toast.error('Error al eliminar');
        }
    };

    const copy = (url) => { navigator.clipboard.writeText(url); toast.success('Copiado al portapapeles'); };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-semibold">Links de pago</h2>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualizar
                    </Button>
                    <Button size="sm" onClick={() => setShowForm(s => !s)}>
                        <Plus className="h-3 w-3 mr-1" />Nuevo link
                    </Button>
                </div>
            </div>

            {showForm && (
                <div className="rounded-xl border bg-card p-5 space-y-4">
                    <h3 className="font-medium text-sm">Crear link de pago</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="text-xs text-muted-foreground">Nombre del link *</label>
                            <Input placeholder="Ej: Suscripción mensual" value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Producto/servicio *</label>
                            <Input placeholder="Nombre del ítem" value={form.item_name}
                                onChange={e => setForm(f => ({ ...f, item_name: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Precio (MXN) *</label>
                            <Input type="number" placeholder="0.00" value={form.item_price}
                                onChange={e => setForm(f => ({ ...f, item_price: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Cantidad</label>
                            <Input type="number" min="1" value={form.item_qty}
                                onChange={e => setForm(f => ({ ...f, item_qty: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Moneda</label>
                            <select className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-background"
                                value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                                <option value="MXN">MXN</option>
                                <option value="USD">USD</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={create} disabled={creating}>
                            {creating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                            Crear
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : links.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">Sin links de pago aún</div>
            ) : (
                <div className="space-y-3">
                    {links.map((l) => (
                        <div key={l.id || l._id} className="rounded-xl border bg-card p-4 flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                <LinkIcon className="h-4 w-4 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate">{l.name || l.number}</div>
                                <div className="text-xs text-muted-foreground">{fmtDate(l.created_at)} · {l.currency}</div>
                                {(l.payment_link || l.link) && (
                                    <a href={l.payment_link || l.link} target="_blank" rel="noreferrer"
                                        className="text-xs text-primary flex items-center gap-1 mt-0.5 truncate">
                                        <ExternalLink className="h-3 w-3 shrink-0" />
                                        {l.payment_link || l.link}
                                    </a>
                                )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                {(l.payment_link || l.link) && (
                                    <Button size="sm" variant="ghost" className="h-7"
                                        onClick={() => copy(l.payment_link || l.link)}>
                                        <Copy className="h-3 w-3" />
                                    </Button>
                                )}
                                <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive"
                                    onClick={() => del(l.id || l._id)}>
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Checkouts tab ────────────────────────────────────────────────────────────
function CheckoutsTab() {
    const [checkouts, setCheckouts] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [creating, setCreating]   = useState(false);
    const [showForm, setShowForm]   = useState(false);
    const [form, setForm] = useState({ concept: '', currency: 'MXN', amounts: '100,200,500', title: '', subtitle: '' });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/ecartpay/checkouts');
            setCheckouts(Array.isArray(data) ? data : (data?.data || data?.checkouts || []));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al cargar checkouts');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!form.concept || !form.amounts) { toast.error('Completa concepto y montos'); return; }
        const amounts = form.amounts.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
        if (amounts.length === 0) { toast.error('Ingresa montos válidos separados por coma'); return; }
        setCreating(true);
        try {
            const { data } = await api.post('/admin/ecartpay/checkouts', {
                concept: form.concept,
                currency: form.currency,
                amounts,
                notify_url: '',
                title: form.title,
                subtitle: form.subtitle,
            });
            toast.success('Checkout creado');
            setShowForm(false);
            setForm({ concept: '', currency: 'MXN', amounts: '100,200,500', title: '', subtitle: '' });
            load();
            if (data.link) {
                navigator.clipboard.writeText(data.link);
                toast.success('Link copiado al portapapeles');
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al crear checkout');
        }
        setCreating(false);
    };

    const copy = (url) => { navigator.clipboard.writeText(url); toast.success('Copiado'); };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-semibold">Checkouts personalizados</h2>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualizar
                    </Button>
                    <Button size="sm" onClick={() => setShowForm(s => !s)}>
                        <Plus className="h-3 w-3 mr-1" />Nuevo checkout
                    </Button>
                </div>
            </div>

            {showForm && (
                <div className="rounded-xl border bg-card p-5 space-y-4">
                    <h3 className="font-medium text-sm">Crear checkout</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <label className="text-xs text-muted-foreground">Concepto *</label>
                            <Input placeholder="Ej: Recarga de saldo" value={form.concept}
                                onChange={e => setForm(f => ({ ...f, concept: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Título (opcional)</label>
                            <Input placeholder="Ej: Mi tienda" value={form.title}
                                onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Subtítulo (opcional)</label>
                            <Input placeholder="Ej: Pagos seguros" value={form.subtitle}
                                onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Montos disponibles (separados por coma) *</label>
                            <Input placeholder="100,200,500,1000" value={form.amounts}
                                onChange={e => setForm(f => ({ ...f, amounts: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Moneda</label>
                            <select className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-background"
                                value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                                <option value="MXN">MXN</option>
                                <option value="USD">USD</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" onClick={create} disabled={creating}>
                            {creating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}Crear
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : checkouts.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">Sin checkouts creados</div>
            ) : (
                <div className="space-y-3">
                    {checkouts.map((c) => (
                        <div key={c.id} className="rounded-xl border bg-card p-4 flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                                <ShoppingCart className="h-4 w-4 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm">{c.concept || c.title}</div>
                                <div className="text-xs text-muted-foreground">
                                    {c.currency} · Montos: {(c.amounts || []).join(', ')} · {fmtDate(c.created_at)}
                                </div>
                                {c.link && (
                                    <a href={c.link} target="_blank" rel="noreferrer"
                                        className="text-xs text-primary flex items-center gap-1 mt-0.5 truncate">
                                        <ExternalLink className="h-3 w-3 shrink-0" />{c.link}
                                    </a>
                                )}
                            </div>
                            {c.link && (
                                <Button size="sm" variant="ghost" className="h-7 shrink-0" onClick={() => copy(c.link)}>
                                    <Copy className="h-3 w-3" />
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Webhooks Events tab ─────────────────────────────────────────────────────
function WebhookEventsTab() {
    const [events, setEvents]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/ecartpay/webhook-events?limit=50');
            setEvents(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error('Error al cargar eventos');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-semibold">Eventos webhook recibidos</h2>
                <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                    <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualizar
                </Button>
            </div>
            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : events.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">Sin eventos recibidos aún. Los webhooks de EcartPay aparecerán aquí.</div>
            ) : (
                <div className="space-y-2">
                    {events.map((e) => (
                        <div key={e.id} className="rounded-xl border bg-card">
                            <button className="w-full flex items-center gap-3 px-4 py-3 text-left"
                                onClick={() => setExpanded(expanded === e.id ? null : e.id)}>
                                <Radio className="h-4 w-4 text-primary shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium">
                                        {e.payload?.status || e.payload?.type || 'evento'}
                                    </span>
                                    <span className="text-xs text-muted-foreground ml-2">
                                        {fmtDate(e.received_at)}
                                    </span>
                                </div>
                                <span className="text-xs text-muted-foreground">{expanded === e.id ? '▲' : '▼'}</span>
                            </button>
                            {expanded === e.id && (
                                <div className="border-t px-4 py-3">
                                    <pre className="text-xs bg-muted rounded-lg p-3 overflow-auto max-h-64">
                                        {JSON.stringify(e.payload, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Settings tab ─────────────────────────────────────────────────────────────
function SettingsTab({ settings, setSettings }) {
    const [form, setForm]     = useState({ public_key: '', private_key: '', environment: 'production' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (settings) setForm(f => ({ ...f, environment: settings.environment || 'production' }));
    }, [settings]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/admin/ecartpay/settings', form);
            toast.success('Configuración guardada');
            const { data } = await api.get('/admin/ecartpay/settings');
            setSettings(data);
            setForm(f => ({ ...f, public_key: '', private_key: '' }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        }
        setSaving(false);
    };

    return (
        <div className="space-y-6 max-w-2xl">
            <div className="rounded-xl border bg-card p-6 space-y-5">
                <h3 className="font-semibold flex items-center gap-2"><Settings className="h-4 w-4" />Claves de API EcartPay</h3>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                    <strong>Seguridad:</strong> Las claves se almacenan cifradas en la base de datos. 
                    Nunca se muestran en texto plano. Para actualizar, ingresa las nuevas claves y guarda.
                </div>

                {settings?.has_public_key && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        Clave pública activa: <code className="font-mono">{settings.public_key_hint}</code>
                        &nbsp;· Ambiente: <strong>{settings.environment}</strong>
                    </div>
                )}

                <div>
                    <label className="text-xs font-medium text-muted-foreground">Clave Pública</label>
                    <Input
                        placeholder={settings?.has_public_key ? '(sin cambios)' : 'pub6a73dd0c…'}
                        value={form.public_key}
                        onChange={e => setForm(f => ({ ...f, public_key: e.target.value }))}
                        className="mt-1 font-mono text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs font-medium text-muted-foreground">Clave Privada</label>
                    <Input
                        type="password"
                        placeholder={settings?.has_private_key ? '(sin cambios)' : 'priv6a73dd0c…'}
                        value={form.private_key}
                        onChange={e => setForm(f => ({ ...f, private_key: e.target.value }))}
                        className="mt-1 font-mono text-sm"
                    />
                </div>
                <div>
                    <label className="text-xs font-medium text-muted-foreground">Entorno</label>
                    <select
                        className="w-full border rounded-md px-3 py-2 text-sm mt-1 bg-background"
                        value={form.environment}
                        onChange={e => setForm(f => ({ ...f, environment: e.target.value }))}
                    >
                        <option value="production">🚀 Producción (ecartpay.com)</option>
                        <option value="sandbox">🧪 Sandbox (sandbox.ecartpay.com)</option>
                    </select>
                </div>
                <Button onClick={save} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Guardar configuración
                </Button>
            </div>

            <div className="rounded-xl border bg-card p-6 space-y-3">
                <h3 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4" />Recursos</h3>
                <div className="space-y-2 text-sm">
                    {[
                        ['Documentación EcartPay', 'https://docs.ecartpay.com/docs/all-about-ecart-pay'],
                        ['Referencia API', 'https://docs.ecartpay.com/reference/getting-started'],
                        ['Panel EcartPay', 'https://ecartpay.com/login'],
                    ].map(([label, url]) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer"
                            className="flex items-center gap-2 text-primary hover:underline">
                            <ExternalLink className="h-3 w-3" />{label}
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AdminEcartPay() {
    const [settings, setSettings] = useState(null);
    const [tab, setTab]           = useState('dashboard');

    useEffect(() => {
        api.get('/admin/ecartpay/settings')
            .then(({ data }) => setSettings(data))
            .catch(() => setSettings(null));
    }, []);

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <h1 className="text-xl font-bold">EcartPay</h1>
                    <p className="text-sm text-muted-foreground">Pasarela de pagos · Órdenes, reembolsos y links de pago</p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex-wrap h-auto gap-1">
                    <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                    <TabsTrigger value="orders">Órdenes</TabsTrigger>
                    <TabsTrigger value="payment-links">Links de pago</TabsTrigger>
                    <TabsTrigger value="checkouts">Checkouts</TabsTrigger>
                    <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
                    <TabsTrigger value="settings">Configuración</TabsTrigger>
                </TabsList>

                <TabsContent value="dashboard">
                    <DashboardTab settings={settings} onSave={setSettings} />
                </TabsContent>
                <TabsContent value="orders">
                    {settings?.has_public_key
                        ? <OrdersTab />
                        : <div className="text-center py-10 text-muted-foreground text-sm">
                            Configura las claves EcartPay en la pestaña <strong>Configuración</strong> primero.
                          </div>
                    }
                </TabsContent>
                <TabsContent value="payment-links">
                    {settings?.has_public_key
                        ? <PaymentLinksTab />
                        : <div className="text-center py-10 text-muted-foreground text-sm">
                            Configura las claves EcartPay en la pestaña <strong>Configuración</strong> primero.
                          </div>
                    }
                </TabsContent>
                <TabsContent value="checkouts">
                    {settings?.has_public_key
                        ? <CheckoutsTab />
                        : <div className="text-center py-10 text-muted-foreground text-sm">
                            Configura las claves EcartPay en la pestaña <strong>Configuración</strong> primero.
                          </div>
                    }
                </TabsContent>
                <TabsContent value="webhooks">
                    <WebhookEventsTab />
                </TabsContent>
                <TabsContent value="settings">
                    <SettingsTab settings={settings} setSettings={setSettings} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
